// DEV-ONLY mock helpers, gated entirely on the REACT_APP_MOCK_WELLNESS env var.
// When the var is unset (production, normal dev) every function here is a no-op
// pass-through, so this file has ZERO effect unless you opt in.
//
// NODE_ENV first, and this is the load-bearing line. REACT_APP_* is a BUILD-TIME
// substitution, CRA reads .env from the repo root during the build, and this repo
// has a TRACKED .env already carrying REACT_APP_API_URL -- so the natural place to
// add a mock variable is a committed file that ships. The deploy path would not
// catch it either: main.yml sets `CI: false` so lint warnings cannot fail the
// build, then syncs straight to S3 and invalidates CloudFront.
//
// react-scripts build sets NODE_ENV=production and a .env file cannot override
// it, so this turns "confirm nobody sets that variable" into "it does not matter
// if they do".

// ---------------------------------------------------------------------------
// Wellness preview (REACT_APP_MOCK_WELLNESS)
// ---------------------------------------------------------------------------
// A production build erases it, so it cannot reach the live dashboard even if
// the variable ends up in the tracked .env.
//
//   unset / ""        -> no mock (default)
//   "FIRST"           -> the first sensor queried
//   "*"               -> every sensor
//   "name1,name2,..." -> those exact sensor names
//
// Run (PowerShell):  $env:REACT_APP_MOCK_WELLNESS="*"; npm start
//
// It ages the newest presence and cooking rows rather than inventing an alert,
// so the signal flows through the REAL rule in wellnessAttention.ts. That
// matters: an OFFLINE sensor still shows nothing, because a sensor that cannot
// see anything is not a quiet kitchen. If a mocked sensor shows no chip, check
// whether it is online before assuming the mock is broken.

const WELLNESS_RAW =
  process.env.NODE_ENV === "production"
    ? ""
    : (process.env.REACT_APP_MOCK_WELLNESS || "").trim();
const WELLNESS_NAMES = WELLNESS_RAW.split(",").map((s) => s.trim()).filter(Boolean);
const WELLNESS_ALL = WELLNESS_NAMES.includes("*");
const WELLNESS_FIRST =
  WELLNESS_NAMES.length === 1 && WELLNESS_NAMES[0].toUpperCase() === "FIRST";

// Comfortably past the 2 and 3 day defaults, and far enough apart that the two
// chips carry visibly different numbers rather than looking like one signal.
const MOCK_NOT_SEEN_DAYS = 4;
const MOCK_NO_COOKING_DAYS = 9;

let wellnessFirstSeen: string | null = null;
let wellnessAnnounced = false;

/** Whether a given sensor should be presented as quiet. */
export function isMockWellnessSensor(name: string): boolean {
  if (!WELLNESS_RAW) return false;
  if (!wellnessAnnounced) {
    wellnessAnnounced = true;
    // eslint-disable-next-line no-console
    console.warn(
      `[devMocks] Mocking wellness alerts for: ` +
        `${WELLNESS_ALL ? "ALL" : WELLNESS_FIRST ? "FIRST queried" : WELLNESS_NAMES.join(", ")}` +
        ` (REACT_APP_MOCK_WELLNESS). Offline sensors still show nothing, by design.` +
        ` Unset the env var to disable.`
    );
  }
  if (WELLNESS_ALL) return true;
  if (WELLNESS_FIRST) {
    if (wellnessFirstSeen === null) wellnessFirstSeen = name;
    return wellnessFirstSeen === name;
  }
  return WELLNESS_NAMES.includes(name);
}

function daysAgoIso(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

type Row = Record<string, unknown>;

/**
 * Replace the newest row's timestamps, or synthesise one when the sensor has no
 * history at all, so the mock works on any sensor rather than only lived-in ones.
 *
 * `pageSize` is checked by the callers: only the single-row "latest" queries are
 * mocked. Ageing a row inside a 1000-row history fetch would distort the session
 * charts and the property report, which is not what anyone asked to preview.
 */
function ageNewestRow<T>(envelope: T, stamps: Row): T {
  const box = (envelope ?? {}) as { data?: unknown };
  const rows = Array.isArray(box.data) ? (box.data as Row[]) : [];
  const head = { ...(rows[0] ?? {}), ...stamps, __mockWellness: true };
  return { ...box, data: [head, ...rows.slice(1)] } as T;
}

/** Age the latest presence row so the resident reads as not seen. */
export function mockWellnessPresence<T>(
  sensorName: string,
  pageSize: number,
  envelope: T
): T {
  if (pageSize !== 1 || !isMockWellnessSensor(sensorName)) return envelope;
  const at = daysAgoIso(MOCK_NOT_SEEN_DAYS);
  return ageNewestRow(envelope, { datetime: at, dateCreated: at, detected: true });
}

/** Age the latest cooking session so the kitchen reads as unused. */
export function mockWellnessLastSession<T>(
  sensorName: string,
  pageSize: number,
  envelope: T
): T {
  if (pageSize !== 1 || !isMockWellnessSensor(sensorName)) return envelope;
  const at = daysAgoIso(MOCK_NO_COOKING_DAYS);
  return ageNewestRow(envelope, { dateStart: at, dateCreated: at });
}
